import { Component, Provide, Vue } from 'vue-property-decorator';
import { IEmployee } from '@/shared/model/employee.model';
import PersonalPinExtendedService from '@/account/spe/employee-authentication/personal-pin-extended.service';
import VuePinCodeInput from '@/account/spe/vue-pin-code-input/vue-pin-code-input.vue';
import EmployeeSearchComponent from '@/account/spe/employee-authentication/employee-search.vue';
import axios from 'axios';

/**
 * External employee authentication page for third-party apps.
 * Reuses Marut's existing PIN UI but redirects back to the caller
 * with a signed JWT instead of creating a local session.
 *
 * Query params: returnUrl, audience, intent, state
 * See docs/employee-auth-integration.md for the full flow.
 */
@Component({
  components: {
    'employee-pin': VuePinCodeInput,
    'employee-search': EmployeeSearchComponent,
  },
})
export default class ExternalAuth extends Vue {
  @Provide('personalPinExtendedService')
  private personalPinExtendedService = () => new PersonalPinExtendedService();

  // Query params
  public returnUrl: string = '';
  public audience: string = '';
  public intent: string = '';
  public state: string = '';

  // UI state
  public selectedEmployee: IEmployee | null = null;
  public pinCode: string = '';
  public loading: boolean = false;
  public error: string | null = null;
  public attempts: number = 0;
  public locked: boolean = false;
  public paramError: string | null = null;

  private maxAttempts: number = 3;

  created() {
    this.returnUrl = (this.$route.query.returnUrl as string) || '';
    this.audience = (this.$route.query.audience as string) || '';
    this.intent = (this.$route.query.intent as string) || '';
    this.state = (this.$route.query.state as string) || '';

    if (!this.returnUrl || !this.audience || !this.intent) {
      this.paramError = 'Missing required parameters (returnUrl, audience, intent)';
    }
  }

  public onSearchError(): void {
    this.error = this.$t('externalAuth.error') as string;
  }

  public onEmployeeSelected(employee: IEmployee): void {
    this.selectedEmployee = employee;
    this.error = null;
    this.pinCode = '';
  }

  public onPinComplete(pin: string): void {
    this.pinCode = pin;
    this.authenticate();
  }

  public authenticate(): void {
    if (!this.selectedEmployee || !this.pinCode || this.locked) return;

    this.loading = true;
    this.error = null;

    axios
      .post('/api/v1/employee-auth-tokens', {
        number: this.selectedEmployee.number,
        password: this.pinCode,
        audience: this.audience,
        intent: this.intent,
      })
      .then(res => {
        const token = res.data.token;
        // Redirect back to caller with token
        const separator = this.returnUrl.includes('?') ? '&' : '?';
        let redirectUrl = `${this.returnUrl}${separator}employeeAuthToken=${encodeURIComponent(token)}`;
        if (this.state) {
          redirectUrl += `&state=${encodeURIComponent(this.state)}`;
        }
        window.location.href = redirectUrl;
      })
      .catch(err => {
        this.attempts++;
        this.pinCode = '';

        if (err.response?.status === 429) {
          this.locked = true;
          this.error = this.$t('externalAuth.locked') as string;
          setTimeout(() => {
            this.locked = false;
            this.attempts = 0;
          }, 60000);
        } else if (err.response?.status === 401) {
          this.error = this.$t('externalAuth.invalidPin') as string;
          if (this.attempts >= this.maxAttempts) {
            this.locked = true;
            this.error = this.$t('externalAuth.locked') as string;
          }
        } else {
          this.error = this.$t('externalAuth.error') as string;
        }
      })
      .finally(() => {
        this.loading = false;
      });
  }

}
